import { sqItemRuntimeStore, sqTableRuntimeStore } from '@/core/core.stores';
import { freezeOutsideProduction } from '@/core/immutability.utilities';
import { ITEM_DATA_STATUS, ITEM_TYPES } from '@/trendData/trendData.constants';
import type { ItemYAxisExtremes } from '@/trendData/itemRuntime.store';

const lookupYAxisExtremes = (
  item: { id: string; isChildOf?: string },
  yAxisExtremes: Record<string, ItemYAxisExtremes>,
): ItemYAxisExtremes | undefined =>
  yAxisExtremes[item.id] ?? (item.isChildOf ? yAxisExtremes[item.isChildOf] : undefined);

/**
 * Returns the computed y-axis extremes for an item, falling back to its parent's entry; children
 * render on their parent's axis (they previously received cloned copies of these fields via
 * CHILD_CLONED_PROPERTIES).
 */
export function getComputedYAxisExtremes(item: { id: string; isChildOf?: string }): ItemYAxisExtremes | undefined {
  return lookupYAxisExtremes(item, sqItemRuntimeStore.getState().yAxisExtremes);
}

/** Returns the currently displayed range as manual customizations, regardless of the chart that owns it. */
export function getFrozenYAxisCustomizations(item: {
  id: string;
  isChildOf?: string;
  itemType: ITEM_TYPES;
}):
  | (Pick<ItemYAxisExtremes, 'yAxisMin' | 'yAxisMax'> & { yAxisConfig?: ItemYAxisExtremes['yAxisConfig'] })
  | undefined {
  // Lane ranges are replaced during recomputation and overlaid onto signal settings. Histogram ranges must
  // survive that recomputation without overriding their own user-entered manual bounds.
  if (item.itemType === ITEM_TYPES.TABLE) {
    const range = sqTableRuntimeStore.getState().autoAxisExtremes[item.id];
    return range ? { yAxisMin: range.min, yAxisMax: range.max } : undefined;
  }
  const computed = getComputedYAxisExtremes(item);
  return computed ? { ...computed, yAxisConfig: { ...computed.yAxisConfig } } : undefined;
}

/**
 * Overlays computed y-axis extremes from a caller-supplied extremes map onto an item. For React
 * components that subscribe via `useYAxisExtremes()`, pass the subscribed map so the dependency
 * stays visible to the React Compiler; `withEffectiveYAxisExtremes` reads the store imperatively
 * and is invisible to it. Items without a computed entry are returned as-is (identity preserved),
 * leaving their persisted fields, user-set extremes or values rehydrated from a workstep, in
 * effect.
 */
export function overlayYAxisExtremes<T extends { id: string; isChildOf?: string }>(
  item: T,
  yAxisExtremes: Record<string, ItemYAxisExtremes>,
): T {
  const computed = lookupYAxisExtremes(item, yAxisExtremes);
  return computed ? freezeOutsideProduction({ ...item, ...computed }) : item;
}

/**
 * Overlays the computed y-axis extremes onto an item so readers see the effective values, reading
 * the extremes from the runtime store imperatively (for non-React consumers and code outside the
 * React Compiler's view).
 */
export function withEffectiveYAxisExtremes<T extends { id: string; isChildOf?: string }>(item: T): T {
  return overlayYAxisExtremes(item, sqItemRuntimeStore.getState().yAxisExtremes);
}

/**
 * Records "a data fetch was just requested" for the item so plugins can recognize the fetch.
 */
export function stampLastFetchRequest(id: string): void {
  sqItemRuntimeStore.actions.setLastFetchRequest(id, new Date().toISOString());
}

/**
 * Records the request state carried by a data-status or results payload; a payload without
 * details clears the stored values.
 */
export function recordDataStatus(
  dataStatus: string,
  payload: {
    id: string;
    message?: unknown;
    warningCount?: unknown;
    warningLogs?: unknown;
    errorType?: unknown;
    errorCategory?: unknown;
    isSharedRequest?: unknown;
    timingInformation?: unknown;
    meterInformation?: unknown;
  },
): void {
  sqItemRuntimeStore.actions.setDataStatus(payload.id, dataStatus, {
    message: payload.message as string | undefined,
    warningCount: payload.warningCount as number | undefined,
    warningLogs: payload.warningLogs,
    errorType: payload.errorType as string | undefined,
    errorCategory: payload.errorCategory as string | undefined,
    isSharedRequest: payload.isSharedRequest as boolean | undefined,
    timingInformation: payload.timingInformation as string | undefined,
    meterInformation: payload.meterInformation as string | undefined,
  });
}

/**
 * Returns the item's data-request status; items that have not started fetching are INITIALIZING.
 */
export function getItemDataStatus(item: { id: string }): string {
  return sqItemRuntimeStore.getState().requestStatus[item.id]?.dataStatus ?? ITEM_DATA_STATUS.INITIALIZING;
}

/**
 * Overlays the item's request status and telemetry (progress, timing and data-read information)
 * onto it so readers see the runtime values. Items with no entries are returned as-is (identity
 * preserved).
 */
export function withRequestState<T extends { id: string }>(item: T): T {
  const { requestStatus, requestTelemetry } = sqItemRuntimeStore.getState();
  const status = requestStatus[item.id];
  const telemetry = requestTelemetry[item.id];
  if (!status && !telemetry) {
    return item;
  }
  return { ...item, ...status, ...telemetry };
}
